/*
 * Shared RBAC matrix helpers
 * ------------------------------------------------------------------
 * Used by RolePermissionEditor (single-role permission configuration screen)
 * to:
 *   - reshape the backend's flat matrix response into { roles, modules, matrix }
 *   - detect a module's "View" feature
 *   - enforce the "child OFF whenever View is OFF" cascade rule
 *
 * `columnKey` is generic on purpose — it's a role id in the multi-role
 * response shape, but any consumer can use these against a single-column
 * matrix the same way.
 */

/**
 * The backend returns normalized flat lists (roles, modules, features,
 * permissions) rather than a pre-nested tree, using generic `id`/`name` fields:
 *   roles:    [{ id, name }]
 *   modules:  [{ id, name, route }]
 *   features: [{ id, module_id, code, display_name }]
 *   permissions: [{ role_id, feature_id, allowed, editable, locked, reason }]
 *
 * Reshapes this into what the UI needs:
 *   - roles: [{ id, name }] (ordered, for columns)
 *   - modules: [{ ...module, features: [feature, ...] }] (ordered, for rows)
 *   - matrix: { [feature.code]: { [role.id]: { allowed, editable, locked, reason } } }
 *   - featureIndex: Map(feature.code -> feature)
 */
/**
 * Feature codes still seeded on the backend but intentionally not surfaced in
 * the Role Permissions UI (e.g. the endpoint they gated no longer enforces
 * them). Filtered out here rather than via a DB migration so no tenant data
 * is touched — the seeded rows are simply never rendered.
 */
const HIDDEN_FEATURE_CODES = ["project.modules.view"];

export const buildMatrixFromResponse = (data) => {
  const roles = Array.isArray(data?.roles) ? data.roles : [];
  const modules = Array.isArray(data?.modules) ? data.modules : [];
  const features = Array.isArray(data?.features)
    ? data.features.filter((feature) => !HIDDEN_FEATURE_CODES.includes(feature?.code))
    : [];
  const permissionCells = Array.isArray(data?.permissions)
    ? data.permissions
    : [];

  const featuresByModule = {};
  features.forEach((feature) => {
    const moduleId = feature.module_id;
    if (!featuresByModule[moduleId]) featuresByModule[moduleId] = [];
    featuresByModule[moduleId].push(feature);
  });

  const orderedModules = modules.map((module) => ({
    ...module,
    features: featuresByModule[module.id] || [],
  }));

  const cellIndex = {};
  permissionCells.forEach((cell) => {
    cellIndex[`${cell.feature_id}::${cell.role_id}`] = cell;
  });

  const matrix = {};
  const featureIndex = new Map();
  features.forEach((feature) => {
    featureIndex.set(feature.code, feature);
    matrix[feature.code] = {};
    roles.forEach((role) => {
      const cell = cellIndex[`${feature.id}::${role.id}`];
      matrix[feature.code][role.id] = {
        allowed: !!cell?.allowed,
        editable: cell?.editable === undefined ? true : !!cell.editable,
        locked: !!cell?.locked,
        reason: cell?.reason ?? null,
      };
    });
  });

  return { roles, modules: orderedModules, matrix, featureIndex };
};

/**
 * Parent-child permission rule: every module has exactly one module-level
 * "access" feature — code shaped "<module>.view" or "<module>.view_module"
 * (2 dot-segments) — and every other feature in that module is a child that
 * depends on it. Both suffixes are recognized since different modules were
 * seeded under each convention (e.g. dashboard.view, leave.view_module) —
 * this does not rename either code, just widens what counts as "the module's
 * parent toggle". Modules without a feature matching this shape simply have
 * no parent/child enforcement.
 */
export const MODULE_VIEW_CODE_PATTERN = /^[^.]+\.(view|view_module)$/;

export const getModuleViewFeature = (module) =>
  (module?.features || []).find((feature) =>
    MODULE_VIEW_CODE_PATTERN.test(feature?.code || ""),
  );

/**
 * Enforces "child OFF whenever the module's View permission is OFF" across a
 * matrix shaped { [feature.code]: { [columnKey]: { allowed, ... } } }, for
 * every column key present (a role id in Role Permissions, or a single
 * synthetic column key like "employee" in per-employee views). Pure function
 * — returns a new matrix, only touching the rows/cells that actually need
 * correcting.
 */
export const normalizeMatrix = (modules, sourceMatrix) => {
  let result = sourceMatrix;
  modules.forEach((module) => {
    const viewFeature = getModuleViewFeature(module);
    if (!viewFeature) return;
    const viewColumnMap = result[viewFeature.code];
    if (!viewColumnMap) return;

    module.features.forEach((feature) => {
      if (feature.code === viewFeature.code) return;
      const columnMap = result[feature.code];
      if (!columnMap) return;

      let updatedColumnMap = null;
      Object.keys(columnMap).forEach((columnKey) => {
        const parentAllowed = !!viewColumnMap[columnKey]?.allowed;
        if (!parentAllowed && columnMap[columnKey]?.allowed) {
          if (!updatedColumnMap) updatedColumnMap = { ...columnMap };
          updatedColumnMap[columnKey] = {
            ...columnMap[columnKey],
            allowed: false,
          };
        }
      });

      if (updatedColumnMap) {
        if (result === sourceMatrix) result = { ...sourceMatrix };
        result[feature.code] = updatedColumnMap;
      }
    });
  });
  return result;
};

/**
 * A child feature is only editable while its module's View permission is
 * allowed for the same column (role, or the single employee column). The
 * View feature itself follows the normal backend-driven editable/locked
 * rules only.
 */
export const isCellEditable = (matrix, cell, module, feature, columnKey) => {
  if (!cell || cell.locked || cell.editable === false) return false;
  if (!module || !feature) return true;
  const viewFeature = getModuleViewFeature(module);
  if (viewFeature && feature.code !== viewFeature.code) {
    const parentCell = matrix[viewFeature.code]?.[columnKey];
    if (!parentCell?.allowed) return false;
  }
  return true;
};

/** True while a child's parent (module View) permission is OFF for this column. */
export const isBlockedByParent = (matrix, module, feature, columnKey) => {
  const viewFeature = getModuleViewFeature(module);
  if (!viewFeature || feature.code === viewFeature.code) return false;
  return !matrix[viewFeature.code]?.[columnKey]?.allowed;
};

/** Role names (from the full matrix) that have `feature.code` allowed by default. */
export const getDefaultRoleAccess = (matrix, roles, featureCode) =>
  roles
    .filter((role) => !!matrix[featureCode]?.[role.id]?.allowed)
    .map((role) => role.name);
